import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_VULTURE_IMAGE} from '../../../configs/fieldBiomeEffects';
import {VULTURE_KETTLE_KIND} from '../../../configs/biomeEffects/vultureKettle';
import {resolveSpawnBox} from './biomeContext';
import {
  TWO_PI,
  lerp,
  pickPointInBox,
  randomIn,
  randomInt,
  randomRange,
  randomSign,
  smoothstep,
  toRadians,
  writeHiddenPose,
  writePose,
} from './biomeMath';

/**
 * СТАЯ ГРИФОВ В ВОСХОДЯЩЕМ ПОТОКЕ (костяная подложка). Чистые функции без
 * Phaser и DOM: по этому описанию частицы каждый кадр ставит сцена
 * (scenes/FieldBiomeEffects).
 *
 * Стая — группа; гриф в ней — две частицы с одной и той же картинкой (арт
 * владельца игры, FIELD_BIOME_VULTURE_IMAGE): сама птица в воздухе и ее тень
 * на земле. Тень — та же картинка, закрашенная черным и бледная: у нее силуэт
 * самой птицы. Картинку грузит сцена, а не код эффектов: не загрузилась —
 * эффект не строится вовсе (см. ensureBiomeTextures).
 *
 * Грифы в стае разного размера: одинаковые читались бы копиями одной
 * картинки. Размеры разведены по ступеням — в любой стае есть и мелкий гриф,
 * и крупный.
 *
 * Все грифы ходят в одну сторону вокруг общей середины, которую сносит ветер
 * матча (тот же, что несет облака); у каждого свой радиус, свое время оборота
 * (на дальнем круге — дольше) и свое «дыхание» круга. Курс птицы — касательная
 * к ее пути: считается по двум близким точкам пути, поэтому верен и на
 * спирали, и при сносе.
 *
 * Грифы слетаются по одному: гриф проступает на широком круге и по спирали
 * сходит на свой. Уходят так же — по одному, по спирали наружу, в своем
 * порядке. Крыльями не машут: только медленный крен.
 *
 * Обычно стая кружит над полем — там, куда смотрит камера. Если камера видит
 * гигантский череп у края пейзажа, часть стай собирается над ним.
 */

const BIRD = 'bird';
const SHADOW = 'shadow';

// Курс птицы — направление между точками ее пути за столько миллисекунд до и
// после момента
const HEADING_STEP_MS = 40;

// Кадр картинки на единицу размаха крыльев: вдоль курса (голова на картинке
// смотрит вправо) и поперек него, вдоль крыльев. Размах — не вся высота кадра:
// вокруг птицы прозрачная кайма. Оба числа — от одного размаха: картинка не
// растягивается
const FRAME_LENGTH_PER_WINGSPAN = FIELD_BIOME_VULTURE_IMAGE.width / FIELD_BIOME_VULTURE_IMAGE.wingspan;
const FRAME_SPAN_PER_WINGSPAN = FIELD_BIOME_VULTURE_IMAGE.height / FIELD_BIOME_VULTURE_IMAGE.wingspan;

/** Насколько точка далека от середины овала: 0 — середина, 1 — кромка */
const resolveOvalDistance = (oval, x, y) => Math.hypot((x - oval.x) / oval.radiusX, (y - oval.y) / oval.radiusY);

const checkIfPointInView = (view, {x, y}) => (
  Boolean(view) && x >= view.left && x <= view.right && y >= view.top && y <= view.bottom
);

/** Номера 0..count-1 в случайном порядке */
const shuffleIndexes = (rng, count) => {
  const indexes = Array.from({length: count}, (_, index) => index);
  for (let index = count - 1; index > 0; index -= 1) {
    const other = Math.min(index, Math.floor(rng() * (index + 1)));
    [indexes[index], indexes[other]] = [indexes[other], indexes[index]];
  }
  return indexes;
};

const createTracks = (context, params) => {
  const {hexSize, wind} = context;
  const plain = context.toWorldOval(params.plain);
  const skull = context.toWorld(params.skull.u, params.skull.v);
  const windAngle = Math.atan2(wind.y, wind.x);
  const shadowOffset = {x: hexSize * params.shadow.offsetFactor.x, y: hexSize * params.shadow.offsetFactor.y};
  // Точки пути пишутся в одни и те же объекты: расчет идет каждый кадр
  const here = {x: 0, y: 0};
  const before = {x: 0, y: 0};
  const after = {x: 0, y: 0};

  /** Точка, придвинутая к середине равнины, если она у ее кромки или за ней */
  const pullIntoPlain = (place) => {
    const distance = resolveOvalDistance(plain, place.x, place.y);
    if (distance <= params.plainInnerShare) {
      return place;
    }
    const share = params.plainInnerShare / distance;
    return {x: plain.x + (place.x - plain.x) * share, y: plain.y + (place.y - plain.y) * share};
  };

  /**
   * Где может быть середина стаи: поле в кадре, отступив от его краев, — круг
   * широкий. Кадр уже двух отступов — остается его середина
   */
  const resolveCentreBox = (view) => {
    const box = resolveSpawnBox(context, view, {fieldMarginFactor: 0, viewMarginFactor: 0});
    const insetX = Math.min(hexSize * params.viewInsetFactor, (box.right - box.left) / 2);
    const insetY = Math.min(hexSize * params.viewInsetFactor, (box.bottom - box.top) / 2);
    return {left: box.left + insetX, right: box.right - insetX, top: box.top + insetY, bottom: box.bottom - insetY};
  };

  const buildGroup = ({rng, view}) => {
    const durationMs = randomIn(rng, params.lifeMs);
    // Над черепом стая собирается, только когда камера на него смотрит: иначе
    // ее никто бы не увидел
    const isOverSkull = checkIfPointInView(view, skull) && rng() < params.skull.chance;
    const driftAngle = windAngle + toRadians(randomRange(rng, -params.drift.spreadDeg, params.drift.spreadDeg));
    const driftSpeed = hexSize * randomIn(rng, params.drift.speedFactor) * (isOverSkull ? params.skull.driftShare : 1);
    const velocity = {x: Math.cos(driftAngle) * driftSpeed, y: Math.sin(driftAngle) * driftSpeed};
    // Точка, над которой стая окажется в середине своей жизни
    let through;
    if (isOverSkull) {
      const jitter = hexSize * params.skull.jitterFactor;
      through = {x: skull.x + randomRange(rng, -jitter, jitter), y: skull.y + randomRange(rng, -jitter, jitter)};
    } else {
      through = pullIntoPlain(pickPointInBox(rng, resolveCentreBox(view)));
    }
    const start = {
      x: through.x - (velocity.x * durationMs) / 2000,
      y: through.y - (velocity.y * durationMs) / 2000,
    };

    const count = randomInt(rng, params.birdCount.min, params.birdCount.max);
    const turnDirection = randomSign(rng);
    const basePhase = rng() * TWO_PI;
    // Уходят грифы в своем порядке, не в том, в каком слетались
    const goneAtMs = [];
    let leaveAtMs = durationMs;
    shuffleIndexes(rng, count).forEach((birdIndex) => {
      goneAtMs[birdIndex] = leaveAtMs;
      leaveAtMs -= randomIn(rng, params.bird.staggerMs);
    });

    // Круги разведены по ступеням от ближнего к дальнему, по грифу на ступень:
    // два грифа на одном круге подолгу шли бы крыло в крыло
    const radiusSteps = shuffleIndexes(rng, count);
    // Размеры — так же, по ступеням от мелкого грифа к крупному, и своим
    // жребием: по простому жребию в иной стае все грифы вышли бы одного размера,
    // а по общему с кругами — крупные всегда кружили бы снаружи
    const scaleSteps = shuffleIndexes(rng, count);

    const particles = [];
    let joinAtMs = 0;
    for (let index = 0; index < count; index += 1) {
      const radiusShare = (radiusSteps[index] + randomIn(rng, params.orbit.stepShare)) / count;
      const centreJitter = hexSize * params.orbit.centreJitterFactor;
      const scaleShare = (scaleSteps[index] + randomIn(rng, params.bird.scaleStepShare)) / count;
      const scale = lerp(params.bird.scale.min, params.bird.scale.max, scaleShare);
      const bird = {
        // Номер грифа в стае: птица и ее тень летят вместе
        birdIndex: index,
        joinAtMs,
        goneAtMs: goneAtMs[index],
        radius: hexSize * lerp(params.orbit.radiusFactor.min, params.orbit.radiusFactor.max, radiusShare),
        // На дальнем круге оборот дольше
        periodMs: lerp(params.orbit.periodMs.min, params.orbit.periodMs.max, radiusShare)
          * (1 + randomRange(rng, -params.orbit.periodJitterShare, params.orbit.periodJitterShare)),
        // Грифы расставлены по кругу, но не поровну
        phase: basePhase + ((index
          + randomRange(rng, -params.orbit.phaseJitterShare, params.orbit.phaseJitterShare)) * TWO_PI) / count,
        centreOffset: {
          x: randomRange(rng, -centreJitter, centreJitter), y: randomRange(rng, -centreJitter, centreJitter),
        },
        breathePeriodMs: randomIn(rng, params.orbit.breathePeriodMs),
        breathePhase: rng() * TWO_PI,
        bankPeriodMs: randomIn(rng, params.bird.bankPeriodMs),
        bankPhase: rng() * TWO_PI,
        // Свой размер грифа (доля обычного размаха) и его размах крыльев
        scale,
        wingspan: hexSize * params.bird.wingspanFactor * scale,
      };
      // Тень — на земле: жетоны и отряды ее закрывают; птица — над отрядами
      particles.push({
        ...bird, kind: SHADOW, layer: FIELD_BIOME_LAYER_KEYS.GROUND, texture: FIELD_BIOME_VULTURE_IMAGE.key,
        tint: params.shadow.tint,
      });
      particles.push({
        ...bird, kind: BIRD, layer: FIELD_BIOME_LAYER_KEYS.AIR, texture: FIELD_BIOME_VULTURE_IMAGE.key,
        tint: params.bird.tint,
      });
      joinAtMs += randomIn(rng, params.bird.staggerMs);
    }

    return {durationMs, start, velocity, turnDirection, isOverSkull, particles};
  };

  /** Где гриф в момент времени (пишет в переданный объект) */
  const resolveBirdPoint = (group, particle, timeMs, out) => {
    // Приходит и уходит гриф по спирали: его круг шире своего
    const settled = Math.min(
      smoothstep(0, params.bird.approachMs, timeMs - particle.joinAtMs),
      smoothstep(0, params.bird.approachMs, particle.goneAtMs - timeMs)
    );
    const breathe = 1 + params.orbit.breatheShare
      * Math.sin((TWO_PI * timeMs) / particle.breathePeriodMs + particle.breathePhase);
    const radius = particle.radius * breathe * lerp(params.bird.approachScale, 1, settled);
    const angle = particle.phase + (group.turnDirection * TWO_PI * timeMs) / particle.periodMs;
    out.x = group.start.x + (group.velocity.x * timeMs) / 1000 + particle.centreOffset.x + Math.cos(angle) * radius;
    out.y = group.start.y + (group.velocity.y * timeMs) / 1000 + particle.centreOffset.y + Math.sin(angle) * radius;
    return out;
  };

  const resolvePose = (group, particle, elapsedMs, out) => {
    const sinceJoinMs = elapsedMs - particle.joinAtMs;
    const untilGoneMs = particle.goneAtMs - elapsedMs;
    if (!(sinceJoinMs > 0) || !(untilGoneMs > 0)) {
      return writeHiddenPose(out);
    }
    const presence = Math.min(
      smoothstep(0, params.bird.fadeMs, sinceJoinMs), smoothstep(0, params.bird.fadeMs, untilGoneMs)
    );
    resolveBirdPoint(group, particle, elapsedMs, here);
    resolveBirdPoint(group, particle, elapsedMs - HEADING_STEP_MS, before);
    resolveBirdPoint(group, particle, elapsedMs + HEADING_STEP_MS, after);
    // Голова на картинке смотрит вправо: поворот спрайта — сам курс птицы
    const heading = Math.atan2(after.y - before.y, after.x - before.x);
    // Крен: размах крыльев на виде сверху чуть укорачивается и возвращается
    const bank = 1 - params.bird.bankDepth
      * (0.5 + 0.5 * Math.sin((TWO_PI * elapsedMs) / particle.bankPeriodMs + particle.bankPhase));
    // Кадр картинки: ширина — вдоль курса, высота — вдоль крыльев
    const length = particle.wingspan * FRAME_LENGTH_PER_WINGSPAN;
    const span = particle.wingspan * FRAME_SPAN_PER_WINGSPAN * bank;

    if (particle.kind === SHADOW) {
      // Тень — силуэт той же птицы в тот же миг: ее размер, курс и крен
      return writePose(
        out, here.x + shadowOffset.x, here.y + shadowOffset.y, params.shadow.alpha * presence, length, span, heading
      );
    }
    return writePose(out, here.x, here.y, params.bird.alpha * presence, length, span, heading);
  };

  return [{
    schedule: {firstDelayMs: params.firstDelayMs, intervalMs: params.intervalMs, maxGroups: params.maxGroups},
    buildGroup,
    resolvePose,
  }];
};

const vultureKettle = {
  kind: VULTURE_KETTLE_KIND,
  // Картинка сцены, не текстура из кода: нет ее — нет и грифов
  textures: [FIELD_BIOME_VULTURE_IMAGE.key],
  createTracks,
};

export default vultureKettle;
